import { EXTENDED_DATA_SCHEMA_TYPES } from './types';
import { getFieldValue } from './fieldHelpers';

/**
 * Get the namespaced attribute key based on the specified extended data scope and attribute key
 * @param {*} scope extended data scope
 * @param {*} key attribute key in extended data
 * @returns a string containing the namespace prefix and the attribute name
 */
export const addScopePrefix = (scope, key) => {
  const scopeFnMap = {
    private: k => `priv_${k}`,
    protected: k => `prot_${k}`,
    public: k => `pub_${k}`,
    meta: k => `meta_${k}`,
  };

  const validKey = key.replace(/\s/g, '_');
  const keyScoper = scopeFnMap[scope];

  return !!keyScoper ? keyScoper(validKey) : validKey;
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This expects submit data to be namespaced (e.g. 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target user type.
 *       These might exists if user swaps between user types before saving the user.
 *
 * @param {Object} data values to look through against userConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetUserType Check that the extended data is relevant for this user type.
 * @param {Object} userFieldConfigs Extended data configurations for user fields.
 * @returns Array of picked extended data fields from submitted data.
 */
export const pickUserFieldsData = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig.limitToUserTypeIds || userTypeConfig.userTypeIds.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, namespacedKey);
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope && !isTargetUserType) {
      // Note: this clears extra custom fields
      // These might exists if user swaps between user types before saving the user.
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};

/**
 * Pick extended data fields from given extended data of the user entity.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This returns namespaced (e.g. 'pub_') initial values for the form.
 *
 * @param {Object} data extended data values to look through against userConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetUserType Check that the extended data is relevant for this user type.
 * @param {Object} userFieldConfigs Extended data configurations for user fields.
 * @returns Array of picked extended data fields
 */
export const initialValuesForUserFields = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, key);
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * Returns props for custom user fields
 * @param {*} userFieldsConfig Configuration for user fields
 * @param {*} userType User type to restrict fields to. If none is passed,
 * only user fields applying to all user types are returned.
 * @param {*} isSignup Optional flag to determine whether the target context
 * is a signup form. Defaults to true.
 * @returns an array of props for CustomExtendedDataField: key, name,
 * fieldConfig, defaultRequiredMessage
 */
export const getPropsForCustomUserFieldInputs = (
  userFieldsConfig,
  userType = null,
  isSignup = true
) => {
  return (
    userFieldsConfig?.reduce((pickedFields, fieldConfig) => {
      const { key, userTypeConfig, schemaType, scope, saveConfig = {} } = fieldConfig || {};
      const namespacedKey = addScopePrefix(scope, key);
      const showField = isSignup ? saveConfig.displayInSignUp : true;

      const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
      const isTargetUserType =
        !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(userType);
      const isUserScope = ['public', 'private', 'protected'].includes(scope);

      return isKnownSchemaType && isTargetUserType && isUserScope && showField
        ? [
            ...pickedFields,
            {
              key: namespacedKey,
              name: namespacedKey,
              fieldConfig: fieldConfig,
            },
          ]
        : pickedFields;
    }, []) || []
  );
};

/**
 * Check if currentUser has permission to post listings.
 * Defined in currentUser's effectivePermissionSet relationship:
 * https://www.sharetribe.com/api-reference/marketplace.html#currentuser-permissionset
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if currentUser has permission to post listings.
 */
export const hasPermissionToPostListings = currentUser => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included to the fetched currentUser entity.'
    );
  }
  return currentUser?.effectivePermissionSet?.attributes?.postListings === 'permission/allow';
};

/**
 * Check if currentUser has permission to initiate transactions.
 * Defined in currentUser's effectivePermissionSet relationship:
 * https://www.sharetribe.com/api-reference/marketplace.html#currentuser-permissionset
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if currentUser has permission to initiate transactions.
 */
export const hasPermissionToInitiateTransactions = currentUser => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included to the fetched currentUser entity.'
    );
  }
  return (
    currentUser?.effectivePermissionSet?.attributes?.initiateTransactions === 'permission/allow'
  );
};

/**
 * Check if currentUser has permission to view listing and user data on a private marketplace.
 * Defined in currentUser's effectivePermissionSet relationship:
 * https://www.sharetribe.com/api-reference/marketplace.html#currentuser-permissionset
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if currentUser has permission to view listing and user data on a private marketplace.
 */
export const hasPermissionToViewData = currentUser => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included to the fetched currentUser entity.'
    );
  }
  return currentUser?.effectivePermissionSet?.attributes?.read === 'permission/allow';
};

/**
 * Check if currentUser has been approved to gain access.
 * I.e. they are not in 'pending-approval' or 'banned' state.
 *
 * If the user is in 'pending-approval' state, they don't have right to post listings and initiate transactions.
 *
 * @param {Object} currentUser API entity.
 * @returns {Boolean} true if currentUser has been approved (state is 'active').
 */
export const isUserAuthorized = currentUser => currentUser?.attributes?.state === 'active';

/**
 * Get the user type configuration for the current user's user type
 * @param {*} config marketplace configuration
 * @param {*} currentUser API entity
 * @returns a single user type configuration, if found
 */
const getCurrentUserTypeConfig = (config, currentUser) => {
  const { userTypes } = config.user;
  return userTypes.find(
    ut => ut.userType === currentUser?.attributes?.profile?.publicData?.userType
  );
};

/**
 * Check if the links for creating a new listing should be shown to the
 * user currently browsing the marketplace.
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if the currentUser's user type, or the anonymous user configuration, is set to see the link
 */
export const showCreateListingLinkForUser = (config, currentUser) => {
  const { topbar } = config;
  const currentUserTypeConfig = getCurrentUserTypeConfig(config, currentUser);

  const { accountLinksVisibility } = currentUserTypeConfig || {};

  return currentUser && accountLinksVisibility
    ? accountLinksVisibility.postListings
    : currentUser
    ? true
    : topbar?.postListingsLink
    ? topbar.postListingsLink.showToUnauthenticatedUsers
    : true;
};

/**
 * Check if payout details tab and payout methods tab should be shown for the user
 * @param {Object} config Marketplace configuration
 * @param {*} currentUser API entity
 * @returns {Object} { showPayoutDetails: Boolean, showPaymentMethods: boolean }
 */
export const showPaymentDetailsForUser = (config, currentUser) => {
  const currentUserTypeConfig = getCurrentUserTypeConfig(config, currentUser);
  const { paymentMethods = true, payoutDetails = true } =
    currentUserTypeConfig?.accountLinksVisibility || {};

  return currentUser
    ? {
        showPayoutDetails: payoutDetails,
        showPaymentMethods: paymentMethods,
      }
    : {
        showPayoutDetails: false,
        showPaymentMethods: false,
      };
};

/**
 * Check if the ShipStation settings tab should be shown for the user.
 * Only shown to users whose user type has postListings enabled.
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {Boolean}
 */
export const showShipStationSettingsForUser = (config, currentUser) => {
  const currentUserTypeConfig = getCurrentUserTypeConfig(config, currentUser);
  const { accountLinksVisibility } = currentUserTypeConfig || {};
  return currentUser ? accountLinksVisibility?.postListings === true : false;
};

/**
 * Check if the Fulfilment settings tab should be shown for the user.
 * Only shown to users whose user type has provider role enabled.
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {Boolean}
 */
export const showFulfilmentSettingsForUser = (config, currentUser) => {
  if (!currentUser) {
    return false;
  }

  const currentUserTypeRoles = getCurrentUserTypeRoles(config, currentUser);
  return currentUserTypeRoles?.provider === true;
};

const ALLOWED_DELIVERY_OPTIONS = ['farmCollection', 'localDelivery', 'courierDelivery'];

const hasValidCoordinates = value => {
  return (
    value && typeof value.lat === 'number' && Number.isFinite(value.lat) &&
    typeof value.lng === 'number' && Number.isFinite(value.lng)
  );
};

/**
 * Check if current user has completed provider fulfilment defaults.
 *
 * Completion rules:
 * - at least one delivery option is selected
 * - if farm collection is selected, collection location has an address
 * - if farm collection is selected, collection geolocation exists with valid lat/lng
 * - if local delivery is selected, local delivery fee exists and is >= 0
 * - if local delivery is selected, local delivery radius exists and is between 0 and 1100 miles
 * - if courier delivery is selected, courier delivery fee exists and is >= 0
 * - freeDeliveryEnabled is boolean
 * - if freeDeliveryEnabled is true, threshold is a positive number
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean}
 */
export const hasCompletedFulfilmentSettings = currentUser => {
  const protectedData = currentUser?.attributes?.profile?.protectedData || {};
  const options = Array.isArray(protectedData.defaultDeliveryOptions)
    ? protectedData.defaultDeliveryOptions.filter(opt => ALLOWED_DELIVERY_OPTIONS.includes(opt))
    : [];
  const location = protectedData.defaultCollectionLocation;
  const geolocation = protectedData.defaultGeolocation;
  const localDeliveryFee = protectedData.defaultLocalDeliveryFeeInSubunits;
  const localDeliveryRadius = protectedData.defaultLocalDeliveryRadiusInMiles;
  const courierDeliveryFee = protectedData.defaultCourierDeliveryFeeInSubunits;
  const freeDeliveryEnabled = protectedData.freeDeliveryEnabled;
  const threshold = protectedData.freeDeliveryThresholdInSubunits;

  const hasDeliveryOptions = options.length > 0;
  const farmCollectionEnabled = options.includes('farmCollection');
  const localDeliveryEnabled = options.includes('localDelivery');
  const courierDeliveryEnabled = options.includes('courierDelivery');
  const hasLocation =
    !farmCollectionEnabled ||
    (typeof location?.address === 'string' && location.address.trim().length > 0);
  const hasGeolocation = !farmCollectionEnabled || hasValidCoordinates(geolocation);
  const hasLocalDeliveryFee =
    !localDeliveryEnabled ||
    (typeof localDeliveryFee === 'number' && Number.isFinite(localDeliveryFee) && localDeliveryFee >= 0);
  const hasLocalDeliveryRadius =
    !localDeliveryEnabled ||
    (typeof localDeliveryRadius === 'number' &&
      Number.isFinite(localDeliveryRadius) &&
      localDeliveryRadius >= 0 &&
      localDeliveryRadius <= 1100);
  const hasCourierDeliveryFee =
    !courierDeliveryEnabled ||
    (typeof courierDeliveryFee === 'number' &&
      Number.isFinite(courierDeliveryFee) &&
      courierDeliveryFee >= 0);
  const hasFreeDeliveryChoice = typeof freeDeliveryEnabled === 'boolean';
  const hasValidThreshold =
    !freeDeliveryEnabled ||
    (typeof threshold === 'number' && Number.isFinite(threshold) && threshold > 0);

  return (
    hasDeliveryOptions &&
    hasLocation &&
    hasGeolocation &&
    hasLocalDeliveryFee &&
    hasLocalDeliveryRadius &&
    hasCourierDeliveryFee &&
    hasFreeDeliveryChoice &&
    hasValidThreshold
  );
};

/**
 * Check if provider should be forced to complete fulfilment settings onboarding
 * before creating new listings.
 *
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {Boolean}
 */
export const mustCompleteFulfilmentSettings = (config, currentUser) => {
  if (!currentUser) {
    return false;
  }

  const currentUserTypeRoles = getCurrentUserTypeRoles(config, currentUser);
  const isProvider = currentUserTypeRoles?.provider === true;

  return isProvider && !hasCompletedFulfilmentSettings(currentUser);
};

/**
 * Check the roles defined for the current user
 * @param {*} config Marketplace configuration
 * @param {*} currentUser API entity
 * @returns Object with attributes 'customer' and 'provider' and boolean values for each
 */
export const getCurrentUserTypeRoles = (config, currentUser) => {
  const currentUserTypeConfig = getCurrentUserTypeConfig(config, currentUser);
  return (
    currentUserTypeConfig?.roles || {
      customer: true,
      provider: true,
    }
  );
};
